<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {computed, ref, watch} from "vue";
import {useStore} from "vuex";
import {useInfiniteScroll} from "@vueuse/core";
import OpportunityRow from "@/components/backlinks/opportunity/partials/OpportunityRow.vue";
import useOpportunityStore from "@/composables/backlinks/opportunity/useOpportunityStore";
import OpportunitiesHeader from "@/components/backlinks/opportunity/partials/header/OpportunitiesHeader.vue";
import InProgressHeader from "@/components/backlinks/opportunity/partials/header/InProgressHeader.vue";
import CompletedHeader from "@/components/backlinks/opportunity/partials/header/CompletedHeader.vue";
import RemovedHeader from "@/components/backlinks/opportunity/partials/header/RemovedHeader.vue";
import useActiveTab from "@/composables/backlinks/opportunity/useActiveTab";
import {getBacklinkOpportunities} from "@/composables/backlinks/opportunity/useBacklinkOpportunity";

const store = useStore();

const { search, orderBy, orderDirection, activeTab, activeCompetitor } = useOpportunityStore();

const { isTabOpportunities, isTabInProgress, isTabCompleted, isTabRemoved } = useActiveTab();

const changeOrderBy = (name) => {
  store.dispatch('backlinkOpportunity/setOrderBy', name);
};

const expandedRow = ref(null);

const toggleRow = (row) => {
  expandedRow.value = expandedRow.value === row.id ? null : row.id;
};

const {data: opportunityData, isLoading} = getBacklinkOpportunities(activeTab, activeCompetitor);

const tableBody = ref(null);
const visibleRows = ref(50);

const opportunities = computed(() =>
  opportunityData.value
    .filter(backlink => backlink.domain.toLowerCase().includes(search.value.toLowerCase()))
    .sort((a, b) => {
      if (orderBy.value === 'competitorCount') {
        // If sort is competitorCount sort by score desc as well
        const primaryComparison = orderDirection.value === 'asc'
          ? a.competitorCount - b.competitorCount
          : b.competitorCount - a.competitorCount;

        if (primaryComparison !== 0) {
          return primaryComparison;
        }

        // Secondary sort by score (always descending)
        return b.score - a.score;
      }

      // Other fields sorting
      if (orderDirection.value === 'asc') {
        return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
      }
      return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
    })
    .slice(0, visibleRows.value)
);

useInfiniteScroll(
  computed(() => tableBody.value?.$el),
  () => {
    visibleRows.value += 50;
  },
  {
    distance: 200,
  }
)

watch(
  activeTab,
  () => visibleRows.value = 50
)
</script>

<template>
  <LoadingBlock v-if="isLoading" />

  <DataTable v-else>
    <TableHeaderRow class="tw-grid-cols-12">
      <TableHeaderCell
        col-span="1"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="score"
        class="tw-justify-center tw-text-center tw-px-1"
        @order-changed="changeOrderBy"
      >
        <span>Domain Authority</span>
      </TableHeaderCell>

      <TableHeaderCell
        :col-span="3"
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="domain"
        @order-changed="changeOrderBy"
      >
        <span>Backlink</span>
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="competitorCount"
        :col-span="isTabRemoved ? 6 : 5"
        @order-changed="changeOrderBy"
      >
        <span>Number of Competitor Overlaps</span>
      </TableHeaderCell>

      <OpportunitiesHeader v-if="isTabOpportunities" />
      <InProgressHeader v-if="isTabInProgress" />
      <CompletedHeader v-if="isTabCompleted" />
      <RemovedHeader v-if="isTabRemoved" />
    </TableHeaderRow>

    <TableBody
      v-if="opportunities?.length > 0"
      ref="tableBody"
      class="tw-max-h-[70vh]"
    >
      <OpportunityRow
        v-for="opportunity in opportunities"
        :key="opportunity.id"
        :backlink="opportunity"
        :is-expanded="expandedRow === opportunity.id"
        @toggle="toggleRow(opportunity)"
      />
    </TableBody>

    <ZeroContent v-if="!opportunities || !opportunities.length">
      Looks like there are no backlinks to display.
    </ZeroContent>
  </DataTable>
</template>
